<script setup lang="ts">
    import { computed } from 'vue'
    import PkEditorRadioButtons from './PkEditorRadioButtons.vue'
    import { DocumentType } from 'models'
    import { useI18n } from 'vue-i18n'

    const modelValue = defineModel<DocumentType | string | undefined>({
        default: undefined,
    })

    const props = defineProps<{
        name: string
        readonly?: boolean
    }>()

    const { t } = useI18n()
    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const options = computed(() => {
        const options = [
            {
                label: $t('documentType.file'),
                value: DocumentType.File,
            },
            {
                label: $t('documentType.url'),
                value: DocumentType.Url,
            },
            {
                label: $t('documentType.plain-text'),
                value: DocumentType.PlainText,
            },
        ]
        if (props.readonly) {
            return options.filter((option) => option.value === modelValue.value)
        }
        return options
    })
</script>

<template>
    <PkEditorRadioButtons
        v-model="modelValue"
        :name
        :options
        :readonly
        modifiers="input-hidden">
        <template #[DocumentType.File]>
            <div>
                <div class="flex items-center gap-4 mb-4">
                    <VvIcon name="ri:file-text-line" class="w-16 h-16" />
                    <strong class="font-semibold">
                        {{ $t('documentType.file') }}
                    </strong>
                </div>
                <p class="text-word-3 text-12">
                    {{ t('fileInfo') }}
                </p>
            </div>
        </template>
        <template #[DocumentType.Url]>
            <div>
                <div class="flex items-center gap-4 mb-4">
                    <VvIcon name="ri:global-line" class="w-16 h-16" />
                    <strong class="font-semibold">
                        {{ $t('documentType.url') }}
                    </strong>
                </div>
                <p class="text-word-3 text-12">
                    {{ t('urlInfo') }}
                </p>
            </div>
        </template>
        <template #[DocumentType.PlainText]>
            <div>
                <div class="flex items-center gap-4 mb-4">
                    <VvIcon name="ri:edit-line" class="w-16 h-16" />
                    <strong class="font-semibold">
                        {{ $t('documentType.plain-text') }}
                    </strong>
                </div>
                <p class="text-word-3 text-12">
                    {{ t('plainTextInfo') }}
                </p>
            </div>
        </template>
    </PkEditorRadioButtons>
</template>

<i18n lang="json">
{
    "en": {
        "fileInfo": "Import a PDF or Microsoft Word (.docx) document from your device.",
        "urlInfo": "Import the content of a web page by crawling its URL.",
        "plainTextInfo": "Write the document content directly as plain text."
    },
    "it": {
        "fileInfo": "Importa un documento PDF o Microsoft Word (.docx) dal tuo dispositivo.",
        "urlInfo": "Importa il contenuto di una pagina web a partire dal suo URL.",
        "plainTextInfo": "Scrivi direttamente il contenuto del documento come testo."
    }
}
</i18n>
